import { PlainButton } from "@jugl-web/ui-kit";
import { useTheme } from "@web-src/themes/useTheme";

import { cx, useAppVariant, useTranslations } from "@jugl-web/utils";
import { FC, useCallback, useEffect, useState } from "react";
import { logger } from "@web-src/utils/logger";
import { ReactComponent as CrossIcon } from "./assets/cross-icon.svg";
import { ReactComponent as LampIcon } from "./assets/lamp-icon.svg";

export const NotificationsPermissionsPopup: FC = () => {
  const { customTheme } = useTheme();
  const { t } = useTranslations();
  const { isCx } = useAppVariant();
  const [isVisible, setIsVisible] = useState(false);
  const [notificationStatus, setNotificationStatus] = useState<
    NotificationPermission | "not-supported"
  >(() => {
    if ("Notification" in window) {
      return Notification.permission;
    }
    return "not-supported";
  });

  // Initialize notification permission listeners
  useEffect(() => {
    navigator.permissions
      ?.query({ name: "notifications" })
      .then((permissionStatus) => {
        permissionStatus.onchange = () => {
          setNotificationStatus(Notification.permission);
        };
      })
      .catch((error) => {
        logger.error("Error querying notification permissions:", error);
      });
  }, []);

  // Check if the notification popup was closed within the last 24 hours
  const wasClosedWithin24Hours = useCallback(() => {
    const lastClosedTimestamp = localStorage.getItem(
      "notificationPopupWasClosed"
    );
    if (!lastClosedTimestamp) return false;

    const lastClosed = new Date(Number(lastClosedTimestamp));
    const now = new Date();
    const hoursDiff = (now.getTime() - lastClosed.getTime()) / (1000 * 60 * 60);

    return hoursDiff < 24;
  }, []);

  useEffect(() => {
    if (
      (notificationStatus === "default" || notificationStatus === "denied") &&
      !wasClosedWithin24Hours()
    ) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [notificationStatus, wasClosedWithin24Hours]);

  const handleClose = useCallback(() => {
    localStorage.setItem("notificationPopupWasClosed", Date.now().toString());
    setIsVisible(false);
  }, []);

  if (!isVisible || isCx) {
    return null;
  }

  return (
    <div
      className="fixed inset-x-4 bottom-0 z-[99] pb-10 transition md:inset-x-auto md:left-1/2 md:-translate-x-1/2"
      onMouseDown={(e) => e.stopPropagation()}
      role="dialog"
    >
      <div
        className={cx(
          "util-prevent-drawer-close relative grid w-full grid-cols-[auto_1fr] items-start gap-x-2 gap-y-3 rounded-2xl py-3 pr-12 pl-4 font-secondary text-sm font-medium text-white md:inline-flex md:w-auto md:items-center md:justify-center md:gap-6 md:px-[120px]",
          {
            "bg-primary-900": customTheme === "blue",
            "bg-primary-800": customTheme === "light",
            "bg-[#333333]": customTheme === "dark",
          }
        )}
      >
        <LampIcon className="mt-0.5 shrink-0 md:mt-0" />
        <span className="min-w-0 text-left leading-5 md:whitespace-nowrap">
          {t({
            id: "notifications.enable-notifications",
            defaultMessage:
              "Enable Notifications for the Jugl.com in your browser",
          })}
        </span>
        {notificationStatus !== "denied" && (
          <PlainButton
            className={cx(
              "col-start-2 justify-self-start rounded-lg bg-white px-4 py-2 font-secondary text-sm font-medium whitespace-nowrap transition-colors hover:bg-white/90",
              {
                "text-primary-900": customTheme === "blue",
                "text-primary-800": customTheme === "light",
                "text-[#333333]": customTheme === "dark",
              }
            )}
            onClick={() => {
              Notification.requestPermission();
              handleClose();
            }}
          >
            {t({
              id: "notifications.open-settings",
              defaultMessage: "Open Settings",
            })}
          </PlainButton>
        )}
        <PlainButton
          className="absolute top-2.5 right-2 md:top-1/2 md:right-6 md:-translate-y-1/2"
          onClick={(e) => {
            handleClose();
            e.stopPropagation();
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <CrossIcon />
        </PlainButton>
      </div>
    </div>
  );
};
